// Bottom preview sheet shown over the expanded map when a marker is
// selected. Renders a compact card by default; tapping the thumbnail
// swaps in a full-width image carousel. Extracted from GuestMapSurface.js.

import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Clock3, Navigation, Star, X } from 'lucide-react';
import { toast } from 'sonner';
import { VenueReviewsModal } from '../VenueReviewsModal';
import { ImageCarousel } from './ImageCarousel';
import { SocialPopup } from './SocialButtons';
import { VenueEventChip } from './VenueEventChip';
import { getVenueImage, getVenueOwnPhotos, venueImagePool } from './venueImage';
import {
  formatVenueCategoryLabel,
  venueOpenStatusLabel,
} from './venueFormatters';
import { openDirections } from './directionsIntent';
import {
  MessageBubbleSvg,
  SocialMediaSvg,
  actionButtonSurface,
} from './mapSurfaceStyles';

const font = "'Inria Serif', serif";
const disabledStyle = { opacity: 0.45, cursor: 'not-allowed' };

export function MapVenuePreviewSheet({
  venue,
  idx,
  distanceText,
  isFarAway = false,
  theme = 'dark',
  onClose,
  onMessage,
  onCardTap,
  venueEvents = [],
  hasActivePass = true,
}) {
  const image = getVenueImage(venue, idx);
  const isDark = theme === 'dark';
  const [isMediaExpanded, setIsMediaExpanded] = useState(false);
  const [showSocials, setShowSocials] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);

  // Build a photo carousel list — prefer the venue's own uploaded photos
  // (main_photo first, then the rest of photo_urls). Fall back to the
  // shared demo pool only when the venue has no uploaded photos.
  const carouselImages = useMemo(() => {
    const ownPhotos = getVenueOwnPhotos(venue);
    if (ownPhotos.length > 0) return ownPhotos;
    const fallback = venueImagePool.filter((p) => p !== image).slice(0, 3);
    return [image, ...fallback];
  }, [venue, image]);

  const cardStyles = useMemo(() => ({
    background: isDark ? 'rgba(14, 14, 22, 0.72)' : 'rgba(255, 255, 255, 0.78)',
    border: '1px solid var(--card-border)',
    boxShadow: 'var(--premium-shadow-main)',
    title: 'var(--text)',
    body: 'var(--text-2)',
    pill: 'var(--accent-surface)',
    pillText: 'var(--accent-text-soft)',
    secondaryBg: isDark ? 'rgba(14, 14, 22, 0.60)' : 'rgba(255,255,255,0.60)',
    secondaryBorder: '1px solid var(--card-border)',
    secondaryText: 'var(--accent-text-soft)',
    overlayButton: isDark ? 'rgba(14, 16, 23, 0.92)' : 'rgba(255,255,255,0.94)',
    subtitle: 'var(--text-2)',
    muted: 'var(--text-2)',
  }), [isDark]);

  const handleDirectionsTap = (event) => {
    event?.stopPropagation?.();
    openDirections(venue);
  };
  const handleMessageTap = (event) => {
    event?.stopPropagation?.();
    if (!hasActivePass) {
      toast('Only guests with an active QR pass can message venues.', {
        duration: 3500,
        id: `msg-gate-${venue?.id || 'venue'}`,
      });
      return;
    }
    onMessage(venue);
  };

  return (
    <AnimatePresence>
      {venue ? (
        <motion.div
          key={venue.id}
          data-testid={`map-venue-preview-${venue.id}`}
          initial={{ opacity: 0, y: 28, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.96 }}
          transition={{ duration: 0.24, ease: 'easeOut' }}
          style={{
            position: 'absolute',
            left: 14,
            right: 14,
            bottom: 'calc(var(--nav-height, 88px) + env(safe-area-inset-bottom, 0px) + 20px)',
            zIndex: 1200,
            borderRadius: 28,
            overflow: 'hidden',
            backdropFilter: 'blur(10px)',
            ...cardStyles,
          }}
        >
          {!isMediaExpanded ? (
            <div style={{ padding: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '112px minmax(0, 1fr)', gap: 14 }}>
                <div style={{ position: 'relative' }}>
                  <button type="button" onClick={() => setIsMediaExpanded(true)} data-testid={`map-venue-preview-${venue.id}-image-button`} style={{ display: 'block', width: '100%', border: 'none', padding: 0, background: 'none', cursor: 'pointer', textAlign: 'left' }}>
                    <img src={image} alt={venue.name} data-testid={`map-venue-preview-${venue.id}-image`} style={{ width: '100%', height: 124, objectFit: 'cover', borderRadius: 22, display: 'block' }} />
                  </button>
                  <button
                    type="button"
                    data-testid={`map-venue-preview-${venue.id}-rating`}
                    onClick={(event) => { event.stopPropagation(); setReviewsOpen(true); }}
                    aria-label="See ratings & reviews"
                    style={{ position: 'absolute', left: 10, bottom: 10, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 9px', borderRadius: 999, background: 'rgba(18,18,28,0.58)', color: '#FFFFFF', fontFamily: font, fontSize: 12, fontWeight: 700, backdropFilter: 'blur(6px)', border: 'none', cursor: 'pointer' }}
                  >
                    {Number(venue.rating) > 0 ? (
                      <>
                        <Star size={12} fill="#F4C54E" color="#F4C54E" />
                        {venue.rating}
                      </>
                    ) : 'New'}
                  </button>
                </div>

                <div
                  data-testid={`map-venue-preview-${venue.id}-info`}
                  role={onCardTap ? 'button' : undefined}
                  tabIndex={onCardTap ? 0 : undefined}
                  onClick={() => onCardTap?.(venue)}
                  style={{ minWidth: 0, display: 'flex', flexDirection: 'column', cursor: onCardTap ? 'pointer' : 'default' }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontFamily: font, fontSize: 19.5, fontWeight: 700, color: cardStyles.title, lineHeight: 1.08, letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{venue.name}</p>
                      <p style={{ margin: '4px 0 0', fontFamily: font, fontSize: 13.5, fontWeight: 700, color: cardStyles.subtitle, letterSpacing: '0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatVenueCategoryLabel(venue)}</p>
                    </div>
                    <button type="button" onClick={(event) => { event.stopPropagation(); onClose(); }} data-testid={`map-venue-preview-${venue.id}-close`} style={{ width: 34, height: 34, borderRadius: '50%', border: 'none', cursor: 'pointer', background: cardStyles.pill, color: cardStyles.pillText, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <X size={16} />
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                    {isFarAway ? (
                      <div
                        data-testid={`map-venue-preview-${venue.id}-far`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderRadius: 999, background: 'rgba(255, 153, 51, 0.16)', color: '#D97A14', fontFamily: font, fontSize: 13, fontWeight: 700 }}
                      >
                        Far from you
                      </div>
                    ) : null}
                    <div data-testid={`map-venue-preview-${venue.id}-distance`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderRadius: 999, background: cardStyles.pill, color: cardStyles.pillText, fontFamily: font, fontSize: 13, fontWeight: 700 }}>
                      <Navigation size={13} />
                      {distanceText}
                    </div>
                    <div data-testid={`map-venue-preview-${venue.id}-open-state`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderRadius: 999, background: cardStyles.pill, color: cardStyles.pillText, fontFamily: font, fontSize: 13, fontWeight: 700 }}>
                      <Clock3 size={13} />
                      {venueOpenStatusLabel(venue)}
                    </div>
                  </div>
                  {venueEvents?.[0] ? (
                    <div style={{ marginTop: 10 }}>
                      <VenueEventChip event={venueEvents[0]} compact />
                    </div>
                  ) : null}
                </div>
              </div>

              <p
                data-testid={`map-venue-preview-${venue.id}-description`}
                onClick={() => onCardTap?.(venue)}
                style={{ margin: '14px 0 0', fontFamily: font, fontSize: 15, lineHeight: 1.48, color: cardStyles.muted, textAlign: 'left', letterSpacing: '0.01em', cursor: onCardTap ? 'pointer' : 'default', overflowWrap: 'break-word', wordBreak: 'break-word' }}
              >{venue.description}</p>

              <div style={{ display: 'grid', gridTemplateColumns: '52px 1fr 52px', gap: 10, marginTop: 14 }}>
                <div style={{ position: 'relative' }}>
                  <button type="button" onClick={(event) => { event.stopPropagation(); setShowSocials((prev) => !prev); }} data-testid={`map-venue-preview-${venue.id}-social-inline`} style={{ width: '100%', height: 50, borderRadius: 18, color: 'var(--text-2)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...actionButtonSurface }}>
                    <SocialMediaSvg size={19} />
                  </button>
                  <AnimatePresence>
                    {showSocials ? <SocialPopup venue={venue} /> : null}
                  </AnimatePresence>
                </div>
                <button type="button" onClick={handleDirectionsTap} data-testid={`map-venue-preview-${venue.id}-directions`} style={{ borderRadius: 18, color: 'var(--accent-text-soft)', fontFamily: font, fontSize: 15, fontWeight: 700, padding: '12px 0', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'var(--accent-surface)', border: '1px solid var(--card-border)', boxShadow: 'var(--premium-shadow-nested)' }}>
                  <Navigation size={16} color="currentColor" strokeWidth={2.2} />
                  Get directions
                </button>
                {venue.allow_messages !== false ? (
                  <button type="button" onClick={handleMessageTap} data-testid={`map-venue-preview-${venue.id}-message-inline`} aria-disabled={!hasActivePass} style={{ borderRadius: 18, color: 'var(--text-2)', cursor: hasActivePass ? 'pointer' : 'not-allowed', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...actionButtonSurface, ...(hasActivePass ? {} : disabledStyle) }}>
                    <MessageBubbleSvg size={19} />
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <div data-testid={`map-venue-preview-${venue.id}-media-expanded`} style={{ position: 'relative', background: '#0F1118' }}>
              <ImageCarousel images={carouselImages} height={220} videoUrl={venue.intro_video_url || ''} />
              <button type="button" onClick={() => setIsMediaExpanded(false)} data-testid={`map-venue-preview-${venue.id}-image-expanded-close`} style={{ position: 'absolute', top: 12, right: 12, width: 36, height: 36, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'rgba(10, 11, 16, 0.62)', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(6px)', zIndex: 5 }}>
                <X size={17} />
              </button>
              <div style={{ padding: '12px 14px 14px', display: 'grid', gridTemplateColumns: '52px 1fr 52px', gap: 10, background: isDark ? 'linear-gradient(180deg, rgba(12, 14, 21, 0.98), rgba(18, 20, 28, 0.98))' : '#FFFFFF' }}>
                <div style={{ position: 'relative' }}>
                  <button type="button" onClick={(event) => { event.stopPropagation(); setShowSocials((prev) => !prev); }} data-testid={`map-venue-preview-${venue.id}-social`} style={{ width: '100%', height: 48, borderRadius: 18, color: 'var(--text-2)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...actionButtonSurface }}>
                    <SocialMediaSvg size={19} />
                  </button>
                  <AnimatePresence>
                    {showSocials ? <SocialPopup venue={venue} /> : null}
                  </AnimatePresence>
                </div>
                <button type="button" onClick={handleDirectionsTap} data-testid={`map-venue-preview-${venue.id}-directions-expanded`} style={{ borderRadius: 18, color: 'var(--accent-text-soft)', fontFamily: font, fontSize: 14.5, fontWeight: 700, padding: '12px 0', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'var(--accent-surface)', border: '1px solid var(--card-border)', boxShadow: 'var(--premium-shadow-nested)' }}>
                  <Navigation size={16} color="currentColor" strokeWidth={2.2} />
                  Get directions
                </button>
                {venue.allow_messages !== false ? (
                  <button type="button" onClick={handleMessageTap} data-testid={`map-venue-preview-${venue.id}-message-expanded`} aria-disabled={!hasActivePass} style={{ borderRadius: 18, color: 'var(--text-2)', cursor: hasActivePass ? 'pointer' : 'not-allowed', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...actionButtonSurface, ...(hasActivePass ? {} : disabledStyle) }}>
                    <MessageBubbleSvg size={19} />
                  </button>
                ) : null}
              </div>
            </div>
          )}
        </motion.div>
      ) : null}
      <VenueReviewsModal open={reviewsOpen} onClose={() => setReviewsOpen(false)} venue={venue} />
    </AnimatePresence>
  );
}
